import { Button, CopyCTA, Form, Grid } from '@livecontrol/core-ui';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useMemo, useRef } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import styled from 'styled-components';
// import { DestinationsList } from '../../../components';
import { MasqueradeToken } from '../../../model';
import { Store, store } from '../../../store';
import type { UntitledEvent } from '../graphql';

const SummaryHeader = styled.div`
  border-bottom: 1px solid var(--white40);
  padding-bottom: 12px;
  margin-bottom: 24px;

  .title {
    font-size: 18px;
    line-height: 22px;
  }

  .subtitle {
    font-size: 14px;
    line-height: 18px;
  }

  .pill-button {
    background-color: var(--white20);
    border: none;
    box-shadow: none;
    width: 100%;
  }
`;

// @fixme: Josh- make this use the distributions field on scheduledEvent
export const AssetDetailsSummary = ({
  untitledEvent
}: {
  untitledEvent: UntitledEvent;
}): React.ReactElement => {
  const { token } = Store.User.useIdentity();
  const { environment } = store.getState();
  const currentTime = useRef(DateTime.now()).current;

  const isEditable = useMemo(
    () => untitledEvent.scheduled_time && DateTime.fromISO(untitledEvent.scheduled_time) > currentTime,
    [currentTime, untitledEvent]
  );

  const masqueradeUrl = useMemo((): string | undefined => {
    if (token) {
      const decoded = MasqueradeToken.decode(token.jwt);

      return decoded
        ? `${environment.DASHBOARD_URI}/masquerade#token=${decoded.jwt}&xref=${untitledEvent.stream?.client_id ?? ''}:${
            untitledEvent.stream?.client_id ?? ''
          }&redirect=%2Fschedule%2Fedit%2F${untitledEvent.id ?? ''}`
        : undefined;
    }

    return undefined;
  }, [environment, token, untitledEvent]);

  const startTime = untitledEvent.scheduled_time ? DateTime.fromISO(untitledEvent.scheduled_time) : undefined;

  const endTime = untitledEvent.scheduled_end ? DateTime.fromISO(untitledEvent.scheduled_end) : undefined;

  const duration = startTime && endTime ? endTime.diff(startTime).shiftTo('hours', 'minutes').toObject() : undefined;

  const muxDashboardUrl = untitledEvent.mux_asset_id ? `https://dashboard.mux.com/environments/599vti/video/assets/${untitledEvent.mux_asset_id}` : null;

  return (
    <div>
      <SummaryHeader className='d-flex justify-content-between'>
        <Grid gridTemplateColumns='1fr' gridTemplateRows='min-content' gap='8px'>
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id={_.uniqueId('tooltip')}>{untitledEvent.location?.user?.organization_name}</Tooltip>
            }
          >
            <a
              className='text-white title text-truncate text-nowrap'
              href={
                environment.production
                  ? `https://${untitledEvent.location?.user?.organization_name ?? ''}.livecontrol.tv/`
                  : `https://${untitledEvent.location?.user?.organization_name ?? ''}.wp.stg.livecontrol.io/`
              }
              target='_blank'
              rel='noreferrer'
            >
              {untitledEvent.location?.user?.organization_name ?? ''} <i className='far fa-external-link ml-4px' />
            </a>
          </OverlayTrigger>
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip id={_.uniqueId('tooltip')}>{untitledEvent.title}</Tooltip>}
          >
            <span className='text-white subtitle text-truncate text-nowrap'>
              {untitledEvent.title}
            </span>
          </OverlayTrigger>
        </Grid>
        <div>
          <Grid
            gridTemplateColumns='repeat(3, 1fr)'
            gap='8px'
            className='mb-12px'
            style={{ direction: 'rtl' }}
          >
            {muxDashboardUrl && (
              <Button
                size='sm'
                className='pill-button'
                style={{ direction: 'ltr' }}
                href={muxDashboardUrl}
                target='_blank'
              >
                MUX URL <i className='far fa-external-link ml-4px' />
              </Button>
            )}
            {untitledEvent.id && (<CopyCTA noIcon toastText='EID Copied' value={`EID:${untitledEvent.id}`}>
              <Button size='sm' className='pill-button'>
                EID: {untitledEvent.id}
              </Button>
            </CopyCTA>)}
            {untitledEvent.stream?.client_id && (<CopyCTA
              noIcon
              toastText='CID Copied'
              value={`CID:${untitledEvent.stream.client_id}`}
            >
              <Button size='sm' className='pill-button'>
                CID: {untitledEvent.stream.client_id}
              </Button>
            </CopyCTA>)}
          </Grid>
          <Grid gridTemplateColumns='repeat(2, 1fr)' gap='8px' style={{ direction: 'rtl' }}>
            {isEditable && masqueradeUrl && (
              <Button
                href={masqueradeUrl}
                target='_blank'
                size='sm'
                variant='outline-light'
                style={{ direction: 'ltr' }}
              >
                <i className='far fa-edit mr-8px' />
                Edit Event
              </Button>
            )}
          </Grid>
        </div>
      </SummaryHeader>
      <Grid
        gridTemplateColumns='repeat(auto-fill, minmax(100px, min(100%/3, max(120px, 100%/5))))'
        rowGap='24px'
      >
        {/* <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Event Type'
            description={
              untitledEvent.event_type === 'Produced' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-camera-movie mr-8px' />
                  <span className='text-truncate text-nowrap'>Produced</span>
                </span>
              ) : untitledEvent.event_type === 'SimLive' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-calendar-day mr-8px' />
                  <span className='text-truncate text-nowrap'>Simulated Live</span>
                </span>
              ) : untitledEvent.event_type === 'Mobile' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-mobile mr-8px' />
                  <span className='text-truncate text-nowrap'>Mobile</span>
                </span>
              ) : untitledEvent.event_type === 'Test' ? (
                <span className='d-flex align-items-center'>
                  <i className='far fa-broadcast-tower mr-8px' />
                  <span className='text-truncate text-nowrap'>Test</span>
                </span>
              ) : (
                <span className='d-flex align-items-center'>
                  <i className='far fa-camera-home mr-8px' />
                  <span className='text-truncate text-nowrap'>Static</span>
                </span>
              )
            }
          />
        </Grid.Item> */}
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Start Time'
            description={
             startTime && (<OverlayTrigger
                placement='bottom'
                overlay={
                  <Tooltip id={_.uniqueId('tooltip')}>
                    Client Time:{' '}
                    {startTime.toFormat('t ZZZZ')}
                  </Tooltip>
                }
              >
                <span>{startTime.toFormat('t ZZZZ')}</span>
              </OverlayTrigger>)
            }
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='End Time'
            description={
              endTime && (<OverlayTrigger
                placement='bottom'
                overlay={
                  <Tooltip id={_.uniqueId('tooltip')}>
                    Client Time:{' '}
                    {endTime.toFormat('t ZZZZ')}
                  </Tooltip>
                }
              >
                <span>{endTime.toFormat('t ZZZZ')}</span>
              </OverlayTrigger>)
            }
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Length'
            description={duration && `${duration.hours && duration.hours > 0 ? `${duration.hours} hr ` : ''} ${
              duration.minutes && Math.floor(duration.minutes) > 0
                ? `${duration.minutes.toFixed(0)} min`
                : ''
            }`}
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Date'
            description={
              <OverlayTrigger
                placement='bottom'
                overlay={
                  <Tooltip id={_.uniqueId('tooltip')}>
                    Client Local Time:{' '}
                    {startTime
                      ?.toLocaleString(DateTime.DATE_SHORT)}
                  </Tooltip>
                }
              >
                <span>{startTime?.toLocaleString(DateTime.DATE_SHORT)}</span>
              </OverlayTrigger>
            }
          />
        </Grid.Item>
        {untitledEvent.stream?.location_id && (
        <Grid.Item>
            <CopyCTA noIcon toastText='Location ID Copied' value={untitledEvent.stream.location_id}>
              <Form.Descriptor
                titleClassName='text-white60 font-weight-light'
                descriptionClassName='text-white mt-8px'
                title='Location'
                description={
                  <OverlayTrigger
                    placement='bottom'
                    overlay={
                      <Tooltip id={_.uniqueId('tooltip')}>
                        <div>{untitledEvent.location?.name}</div>
                        <div>{untitledEvent.stream.location_id}</div>
                      </Tooltip>
                    }
                  >
                    <button
                      type='button'
                      className='x-pseudo text-truncate text-nowrap w-100 text-left'
                    >
                      {untitledEvent.location?.name}
                    </button>
                  </OverlayTrigger>
                }
              />
            </CopyCTA>
        </Grid.Item>
        )}
        <Grid.Item>
          {untitledEvent.scheduled_event_password ? (
            <CopyCTA noIcon toastText='Password Copied' value={untitledEvent.scheduled_event_password}>
              <Form.Descriptor
                titleClassName='text-white60 font-weight-light'
                descriptionClassName='text-white mt-8px'
                title='Password'
                description={
                  <OverlayTrigger
                    placement='bottom'
                    overlay={
                      <Tooltip id={_.uniqueId('tooltip')}>{untitledEvent.scheduled_event_password}</Tooltip>
                    }
                  >
                    <button type='button' className='x-pseudo'>
                      Yes
                    </button>
                  </OverlayTrigger>
                }
              />
            </CopyCTA>
          ) : (
            <Form.Descriptor
              titleClassName='text-white60 font-weight-light'
              descriptionClassName='text-white mt-8px'
              title='Password'
              description='No'
            />
          )}
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Chat'
            description='Disabled'
          />
        </Grid.Item>
        <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Event Status'
            description={untitledEvent.hidden ? 'Private' : 'Public'}
          />
        </Grid.Item>
        {/* <Grid.Item>
          <Form.Descriptor
            titleClassName='text-white60 font-weight-light'
            descriptionClassName='text-white mt-8px'
            title='Destinations'
            description={<DestinationsList {...untitledEvent} />}
          />
        </Grid.Item> */}
      </Grid>
    </div>
  );
};
